import { type Box, buildParentMap, type LivePeer, worldBox } from "@cva/shared";
import {
  Check,
  CloudOff,
  Eye,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Sparkles,
  TriangleAlert,
  Undo2,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { initials } from "../components/app-shell";
import { Button } from "../components/ui/button";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { useLive, useLiveSync } from "./live";
import { useEditor, useEditorApi } from "./store";
import type { ClipSelection } from "./video/store";
import { useVideoApi } from "./video/store";

/** Saved / saving / offline, next to undo and redo. */
export function LiveIndicator() {
  const status = useLive((s) => s.status);
  const unsaved = useLive((s) => s.unsaved);
  const access = useLive((s) => s.access);
  let icon = <Check className="size-3.5" />;
  let label = "Gespeichert";
  let className = "text-zinc-500";
  let title = "Alle Änderungen sind gespeichert. Andere sehen sie sofort.";
  if (status === "connecting") {
    icon = <Loader2 className="size-3.5 animate-spin" />;
    label = "Verbinde …";
    title = "Verbindung zum Server wird hergestellt";
  } else if (status === "offline") {
    icon = <CloudOff className="size-3.5" />;
    label = unsaved ? "Offline – wird nachgeholt" : "Offline";
    className = "text-amber-600";
    title = "Keine Verbindung. Deine Änderungen werden übertragen, sobald sie wieder besteht.";
  } else if (status === "closed") {
    icon = <TriangleAlert className="size-3.5" />;
    label = "Getrennt";
    className = "text-red-600";
    title = "Die Verbindung wurde beendet.";
  } else if (access === "view") {
    icon = <Eye className="size-3.5" />;
    label = "Nur ansehen";
    title = "Du siehst Änderungen der anderen live, kannst aber nichts ändern.";
  } else if (unsaved) {
    icon = <Loader2 className="size-3.5 animate-spin" />;
    label = "Speichert …";
  }
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${className}`} title={title}>
      {icon}
      {label}
    </span>
  );
}

/** The connection was ended by the server (file in the trash, access removed). */
export function LiveClosedBanner() {
  const reason = useLive((s) => s.closedReason);
  if (!reason) return null;
  return (
    <div className="flex items-center justify-center gap-3 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-900">
      <TriangleAlert className="size-4 shrink-0" />
      <span>{reason} Änderungen sind nicht mehr möglich.</span>
      <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
        <RefreshCw /> Neu laden
      </Button>
    </div>
  );
}

function PeerAvatar({ peer, size = "size-7 text-xs" }: { peer: LivePeer; size?: string }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${size}`}
      style={{ backgroundColor: peer.color }}
    >
      {initials(peer.name) || "?"}
    </span>
  );
}

/** Who else is in the file. A click jumps to where they are. */
export function PeerAvatars() {
  const peers = useLive((s) => s.peers);
  const self = useLive((s) => s.self);
  const store = useEditorApi();
  const video = useVideoApi();
  const pages = useEditor((s) => s.doc.pages);
  // One avatar per person (several tabs count once); not oneself in another tab.
  const people = useMemo(() => {
    const byUser = new Map<string, LivePeer>();
    for (const peer of peers) {
      if (peer.kind === "agent") continue;
      if (peer.userId === self?.userId) continue;
      const known = byUser.get(peer.userId);
      if (!known || (known.mode === "view" && peer.mode === "edit")) byUser.set(peer.userId, peer);
    }
    return [...byUser.values()];
  }, [peers, self?.userId]);
  const agents = useMemo(() => peers.filter((peer) => peer.kind === "agent"), [peers]);
  if (people.length === 0 && agents.length === 0) return null;
  const follow = (peer: LivePeer) => {
    if (video && peer.s.time !== null) video.getState().seek(peer.s.time);
    const index = pages.findIndex((page) => page.id === peer.s.page);
    if (index >= 0) store.getState().setPage(index);
  };
  const shown = people.slice(0, 5);
  return (
    <div className="flex items-center -space-x-1.5">
      {agents.map((peer) => (
        <AgentAvatar key={peer.client} peer={peer} />
      ))}
      {shown.map((peer) => {
        const page = pages.findIndex((entry) => entry.id === peer.s.page);
        const where = video ? "" : page >= 0 && pages.length > 1 ? ` · Seite ${page + 1}` : "";
        return (
          <button
            key={peer.userId}
            type="button"
            onClick={() => follow(peer)}
            title={`${peer.name} ${peer.mode === "edit" ? "bearbeitet" : "sieht sich"} die Datei gerade${peer.mode === "edit" ? "" : " an"}${where} – klicken, um dorthin zu springen`}
            className="rounded-full ring-2 ring-white transition-transform hover:z-10 hover:scale-110"
          >
            <PeerAvatar peer={peer} />
          </button>
        );
      })}
      {people.length > shown.length ? (
        <span
          className="inline-flex size-7 items-center justify-center rounded-full bg-zinc-200 text-[11px] font-semibold text-zinc-700 ring-2 ring-white"
          title={people
            .slice(shown.length)
            .map((peer) => peer.name)
            .join(", ")}
        >
          +{people.length - shown.length}
        </span>
      ) : null}
    </div>
  );
}

/**
 * An AI assistant working in the file as the person that connected it
 * (docs/23-mcp-v2.md): avatar with a status sentence, a menu to follow it,
 * stop it and take back its changes.
 */
function AgentAvatar({ peer }: { peer: LivePeer }) {
  const live = useLiveSync();
  const access = useLive((state) => state.access);
  const store = useEditorApi();
  const pages = useEditor((state) => state.doc.pages);
  const [following, setFollowing] = useState(false);
  const stopped = peer.s.stopped;
  const page = peer.s.page;
  // Follow mode: the own editor moves to the page the assistant works on.
  useEffect(() => {
    if (!following || !page) return;
    const index = pages.findIndex((entry) => entry.id === page);
    if (index >= 0 && index !== store.getState().pageIndex) store.getState().setPage(index);
  }, [following, page, pages, store]);
  const canControl = access === "edit";
  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownTrigger asChild>
          <button
            type="button"
            title={`${peer.name} – ${stopped ? "gestoppt" : (peer.s.note ?? "arbeitet in dieser Datei mit")}`}
            className="relative rounded-full ring-2 ring-white transition-transform hover:z-10 hover:scale-110"
          >
            <span
              className={`inline-flex size-7 items-center justify-center rounded-full text-white ${stopped ? "opacity-50" : ""}`}
              style={{ backgroundColor: peer.color }}
            >
              <Sparkles className={`size-4 ${peer.s.note && !stopped ? "animate-pulse" : ""}`} />
            </span>
            {following ? (
              <span className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full bg-zinc-900 ring-2 ring-white" />
            ) : null}
          </button>
        </DropdownTrigger>
        <DropdownContent>
          <div className="px-2 py-1.5 text-xs text-zinc-500">
            <div className="font-medium text-zinc-800">{peer.name}</div>
            <div>{stopped ? "Gestoppt" : (peer.s.note ?? "Bereit")}</div>
          </div>
          <DropdownItem onSelect={() => setFollowing((value) => !value)}>
            <UserRound /> {following ? "Nicht mehr folgen" : "Seite folgen"}
          </DropdownItem>
          <DropdownItem
            disabled={!canControl}
            onSelect={() => live?.agentStop(peer.client, !stopped)}
          >
            {stopped ? <Play /> : <Pause />}{" "}
            {stopped ? "Weiterarbeiten lassen" : `${peer.name.split(" (")[0]} stoppen`}
          </DropdownItem>
          <DropdownItem disabled={!canControl} onSelect={() => live?.agentUndo(peer.client)}>
            <Undo2 /> Änderungen von {peer.name.split(" (")[0]} rückgängig
          </DropdownItem>
        </DropdownContent>
      </DropdownMenu>
      {peer.s.note && !stopped ? (
        <span className="pointer-events-none absolute top-full right-0 z-30 mt-1.5 w-max max-w-60 truncate rounded-md bg-zinc-900 px-2 py-1 text-[11px] text-white shadow-md">
          {peer.s.note}
        </span>
      ) : null}
    </div>
  );
}

function NameTag({ peer, zoom, text }: { peer: LivePeer; zoom: number; text?: string }) {
  return (
    <span
      className="absolute left-0 rounded px-1.5 py-0.5 text-[11px] leading-tight font-medium whitespace-nowrap text-white shadow-sm"
      style={{
        backgroundColor: peer.color,
        bottom: "100%",
        transform: `scale(${1 / zoom})`,
        transformOrigin: "0 100%",
        marginBottom: 2 / zoom,
      }}
    >
      {text ?? peer.name}
    </span>
  );
}

function PeerBox({
  peer,
  box,
  zoom,
  label,
}: {
  peer: LivePeer;
  box: Box;
  zoom: number;
  label?: string | null;
}) {
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
        outline: `${2 / zoom}px solid ${peer.color}`,
      }}
    >
      {label === null ? null : <NameTag peer={peer} zoom={zoom} text={label} />}
    </div>
  );
}

function PeerCursor({ peer, zoom }: { peer: LivePeer; zoom: number }) {
  const cursor = peer.s.cursor;
  if (!cursor) return null;
  return (
    <div
      className="pointer-events-none absolute top-0 left-0 z-30"
      style={{
        transform: `translate(${cursor.x}px, ${cursor.y}px) scale(${1 / zoom})`,
        transformOrigin: "0 0",
        transition: "transform 90ms linear",
      }}
    >
      <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden="true">
        <path
          d="M1 1 L1 16 L5 12 L8 19 L11 17.8 L8 11 L14 11 Z"
          fill={peer.color}
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="absolute top-4 left-3.5 rounded px-1.5 py-0.5 text-[11px] leading-tight font-medium whitespace-nowrap text-white shadow-sm"
        style={{ backgroundColor: peer.color }}
      >
        {peer.name}
      </span>
    </div>
  );
}

/**
 * On the canvas (document coordinates): what the others have selected, the
 * text they are typing in and their mouse pointers.
 */
export function PeerCanvasLayer({ zoom }: { zoom: number }) {
  const peers = useLive((s) => s.peers);
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const pageId = doc.pages[pageIndex]?.id;
  const here = useMemo(() => peers.filter((peer) => peer.s.page === pageId), [peers, pageId]);
  const parents = useMemo(() => (here.length > 0 ? buildParentMap(doc) : null), [doc, here]);
  if (!parents || here.length === 0) return null;
  return (
    <>
      {here.map((peer) => {
        const ids = peer.s.editing ? [peer.s.editing] : peer.s.selection;
        return (
          <div key={peer.client}>
            {ids.map((id, index) => {
              const box = worldBox(doc, id, parents);
              if (!box) return null;
              const label = peer.s.editing
                ? `${peer.name} schreibt …`
                : index === 0
                  ? peer.kind === "agent" && peer.s.note
                    ? `${peer.name}: ${peer.s.note}`
                    : peer.name
                  : null;
              return <PeerBox key={id} peer={peer} box={box} zoom={zoom} label={label} />;
            })}
            <PeerCursor peer={peer} zoom={zoom} />
          </div>
        );
      })}
    </>
  );
}

/** The first other person who has this clip selected (timeline). */
export function usePeerOnClip(track: NonNullable<ClipSelection>["track"], id: string) {
  return useLive((s) =>
    s.peers.find((peer) => peer.s.clip?.track === track && peer.s.clip.id === id),
  );
}

/** Coloured frame and name on a clip someone else has selected. */
export function PeerClipMark({ peer }: { peer: LivePeer | undefined }) {
  if (!peer) return null;
  return (
    <span
      className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit]"
      style={{ boxShadow: `inset 0 0 0 2px ${peer.color}` }}
    >
      <span
        className="absolute right-1 bottom-0.5 max-w-[80%] truncate rounded px-1 text-[9px] leading-tight font-medium text-white"
        style={{ backgroundColor: peer.color }}
      >
        {peer.name}
      </span>
    </span>
  );
}

/** Playheads of the others on the timeline. */
export function PeerPlayheads({ xOf, height }: { xOf: (time: number) => number; height: number }) {
  const peers = useLive((s) => s.peers);
  return (
    <>
      {peers.map((peer) =>
        peer.s.time === null ? null : (
          <div
            key={peer.client}
            className="pointer-events-none absolute top-0 z-10"
            style={{ left: xOf(peer.s.time) - 1, height, transition: "left 200ms linear" }}
            title={peer.name}
          >
            <span
              className="absolute top-0 -left-2 flex size-4 items-center justify-center rounded-full text-[8px] font-bold text-white ring-1 ring-white"
              style={{ backgroundColor: peer.color }}
            >
              {initials(peer.name).slice(0, 1) || "?"}
            </span>
            <div className="h-full w-0.5 opacity-70" style={{ backgroundColor: peer.color }} />
          </div>
        ),
      )}
    </>
  );
}
